import { Switch } from "@headlessui/react";
import clsx from "clsx";
import { TouchEvent, useRef } from "react";
import { FCC } from "types";

// Minimum horizontal movement before a touch counts as a swipe rather than a
// tap — below this, the browser's own synthetic click still drives the toggle.
const SWIPE_THRESHOLD_PX = 10;

interface Props {
  enabled: boolean;
  onChange: (e: any) => void;
  disabled?: boolean;
  ariaLabel?: string;
  size?: "sm" | "lg";
}

const sizeClassName = {
  // dire-switch, size=lg: two size-8 (32px) circles with a space-1 (4px) gap.
  lg: { knob: "size-8", travel: "translate-x-9" },
  // dire-switch, size=sm: two size-6 (24px) circles with a space-1 (4px) gap.
  sm: { knob: "size-6", travel: "translate-x-7" },
} as const;

// The track carries the state — dark when on, a faint tint when off.
// Disabled always wins, even when the toggle is also on: a disabled-but-on
// toggle must not read as a live surface.
const stateClassName = {
  disabled: {
    track: "cursor-not-allowed bg-surface-quaternary",
    knob: "bg-surface-secondary",
  },
  on: {
    track: "cursor-pointer bg-surface-primary",
    knob: "bg-surface-primary-inverse group-hover:bg-foreground-secondary-inverse group-focus-visible:bg-foreground-secondary-inverse",
  },
  off: {
    track: "cursor-pointer bg-surface-secondary",
    knob: "bg-surface-primary group-hover:bg-foreground-secondary group-focus-visible:bg-foreground-secondary",
  },
} as const;

export const Toggle: FCC<Props> = ({
  enabled,
  onChange,
  disabled,
  ariaLabel,
  size = "lg",
}) => {
  const { knob, travel } = sizeClassName[size];
  const stateClasses =
    stateClassName[disabled ? "disabled" : enabled ? "on" : "off"];
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isSwiping = useRef(false);

  const handleTouchStart = (e: TouchEvent) => {
    if (disabled) {
      return;
    }

    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (
      disabled ||
      touchStartX.current === null ||
      touchStartY.current === null
    ) {
      return;
    }

    const deltaX = Math.abs(e.touches[0].clientX - touchStartX.current);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartY.current);

    // Only a predominantly horizontal gesture counts as a swipe -- otherwise
    // a vertical scroll that starts on the toggle and wobbles sideways a bit
    // would get hijacked into flipping it instead of scrolling the page.
    if (deltaX > SWIPE_THRESHOLD_PX && deltaX > deltaY) {
      isSwiping.current = true;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (disabled || touchStartX.current === null) {
      return;
    }

    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // A plain tap: let the browser's own synthetic click toggle it as usual.
    if (!isSwiping.current) {
      return;
    }

    // The gesture crossed the threshold at some point, so this was a drag
    // rather than a tap -- always stop the synthetic click that follows, even
    // if the finger then drifted back toward the start (below).
    e.preventDefault();

    // Only resolve a new state if the release point is still decisively past
    // the threshold; a gesture that crosses it and drifts back near the
    // start shouldn't flip the toggle over a stray leftover pixel or two.
    if (Math.abs(deltaX) <= SWIPE_THRESHOLD_PX) {
      return;
    }

    const swipedToEnabled = deltaX > 0;

    if (swipedToEnabled !== enabled) {
      onChange(swipedToEnabled);
    }
  };

  return (
    <Switch
      data-testid="toggle"
      className={clsx(
        stateClasses.track,
        "group inline-flex flex-shrink-0 items-center gap-1 rounded-full p-1 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-primary focus-visible:ring-offset-2",
      )}
      checked={enabled}
      onChange={onChange}
      disabled={disabled}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {ariaLabel && <span className="sr-only">{ariaLabel}</span>}
      {/* The knob sits over its own slot and slides across the gap onto the
          second slot below — the second slot itself never renders anything,
          it only reserves the space the DS component gives it. */}
      <span
        className={clsx(
          enabled ? travel : "translate-x-0",
          stateClasses.knob,
          knob,
          "pointer-events-none inline-block rounded-full ring-0 transition-transform duration-200 ease-in-out",
        )}
        aria-hidden="true"
      />
      <span className={knob} aria-hidden="true" />
    </Switch>
  );
};
